/*
 * The application, and the one decision it makes before anything else: is
 * somebody signed in?
 *
 * Until `useMe` has answered, nothing renders — not the shell, not a redirect.
 * Showing the sign-in page for the half-second it takes to check a cookie
 * would make every reload flash a login form at somebody who is already signed
 * in, which reads as having been logged out.
 *
 * The live price socket is opened inside the signed-in branch only. It
 * authenticates with the session cookie, so opening it before there is one is
 * a connection the server closes immediately and the client then retries.
 */

import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useConnect, useMe } from "./api/hooks";
import { LiveProvider } from "./api/stream";
import { useToast } from "./components/Toast";
import { loginFailure, takeRequestToken } from "./lib/kiteReturn";
import { Layout } from "./Layout";
import { ChartsPage } from "./pages/ChartsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { HelpPage } from "./pages/HelpPage";
import { OrdersPage } from "./pages/OrdersPage";
import { PeoplePage } from "./pages/PeoplePage";
import { PositionsPage } from "./pages/PositionsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SignInPage } from "./pages/SignInPage";
import { SignalsPage } from "./pages/SignalsPage";
import { StrategiesPage } from "./pages/StrategiesPage";
import { StrategyPage } from "./pages/StrategyPage";

export function App() {
  const me = useMe();

  if (me.isPending) {
    return (
      <div className="signin-page">
        <span className="spinner" aria-label="Loading" />
      </div>
    );
  }

  if (!me.data) return <SignInPage />;

  const user = me.data.user;

  return (
    <LiveProvider>
      <ZerodhaReturn />
      <Layout user={user}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/strategies" element={<StrategiesPage />} />
          <Route path="/strategies/:id" element={<StrategyPage />} />
          <Route path="/positions" element={<PositionsPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/signals" element={<SignalsPage />} />
          <Route path="/charts" element={<ChartsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/help" element={<HelpPage />} />
          {user.isAdmin && <Route path="/people" element={<PeoplePage />} />}
          {/* /algo/ is where Zerodha's redirect URL still points, left over
              from when this was a tool on tools.hkmjerp.in. It is kept as a
              real route rather than left to the catch-all so an old bookmark
              and an unchanged Kite app both land somewhere sensible. */}
          <Route path="/algo" element={<Navigate to="/" replace />} />
          <Route path="/algo/*" element={<Navigate to="/" replace />} />
          <Route path="*" element={<KeepSearch />} />
        </Routes>
      </Layout>
    </LiveProvider>
  );
}

/**
 * Spends the token Zerodha sent back, once, wherever it landed.
 *
 * Mounted above the routes rather than on the dashboard, because the path it
 * arrives on belongs to the Kite Connect app's configuration and not to this
 * application — see lib/kiteReturn.ts, which is where the token is actually
 * rescued from the URL.
 */
function ZerodhaReturn() {
  const connect = useConnect();
  const toast = useToast();

  useEffect(() => {
    const failure = loginFailure();
    if (failure) {
      toast.fail(new Error(failure));
      return;
    }
    const token = takeRequestToken();
    if (!token) return;

    connect.mutate(token, {
      onSuccess: () => toast.ok("Connected to Zerodha for today."),
      onError: (e) => toast.fail(e),
    });
    // Once, on mount. `takeRequestToken` is itself single-use, so a re-run
    // would find nothing, but saying so here is clearer than relying on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/**
 * An unknown path goes to the dashboard, keeping its query string.
 *
 * The plain `<Navigate to="/">` this replaces silently dropped the search
 * params, which is exactly how a Zerodha request token went missing.
 */
function KeepSearch() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
}
